import React, { useState, MouseEvent, ChangeEvent, ReactElement } from 'react'
import { connectPagination } from 'react-instantsearch-dom'
import uuid from 'uuid'
import { Roundel } from '@mindfulchefuk/design-system/Roundel/Roundel'
import {
  colors,
  Icon,
  ResetButton,
  Select,
  Stack,
} from '@mindfulchefuk/design-system'

type TPagination = {
  currentRefinement: number
  nbPages: number
  refine: (page: number) => void
}
// Exported for testing
export const UnconnectedPagination = ({
  currentRefinement,
  nbPages,
  refine,
}: TPagination): ReactElement => {
  const [identifier] = useState(uuid.v4())

  const prevPage = currentRefinement - 1
  const nextPage = currentRefinement + 1

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    refine(parseInt(event.currentTarget.dataset.page, 10))
  }

  const onChange = (event: ChangeEvent<HTMLSelectElement>) => {
    refine(parseInt(event.target.value, 10))
  }

  const pageOptions = new Array(nbPages).fill(null).map((_, index) => {
    const page = index + 1
    return <option key={`pagination-${identifier}-${page}`}>{page}</option>
  })

  return (
    <Stack spacing={8} direction="row" align="center" py={36} justify="center">
      <ResetButton
        data-testid="pagination-first-page"
        type="button"
        disabled={currentRefinement === 1}
        onClick={onClick}
        data-page="1"
      >
        <Roundel
          disableOpacity
          filled
          size={48}
          fontSize={30}
          color={
            currentRefinement === 1 ? colors['pepper-100'] : colors.aubergine
          }
          disabled={currentRefinement === 1}
        >
          <Icon
            type="chevron-left"
            color="white"
            size={20}
            position="relative"
            left={6}
          />
          <Icon
            type="chevron-left"
            color="white"
            size={20}
            left={-6}
            position="relative"
          />
        </Roundel>
      </ResetButton>
      <ResetButton
        data-testid="pagination-prev-page"
        type="button"
        data-page={prevPage}
        onClick={onClick}
        disabled={prevPage <= 0}
      >
        <Roundel
          disableOpacity
          filled
          size={48}
          fontSize={30}
          color={prevPage <= 0 ? colors['pepper-100'] : colors.aubergine}
          disabled={prevPage <= 0}
        >
          <Icon
            type="chevron-left"
            color="white"
            size={20}
            position="relative"
            left={-2}
          />
        </Roundel>
      </ResetButton>

      <Select
        onChange={onChange}
        value={currentRefinement}
        data-testid="pagination-select"
      >
        {pageOptions}
      </Select>

      <ResetButton
        data-testid="pagination-next-page"
        type="button"
        data-page={nextPage}
        onClick={onClick}
        disabled={nextPage > nbPages}
      >
        <Roundel
          disableOpacity
          filled
          size={48}
          fontSize={30}
          color={nextPage > nbPages ? colors['pepper-100'] : colors.aubergine}
          disabled={nextPage > nbPages}
        >
          <Icon
            type="chevron-right"
            color="white"
            size={20}
            position="relative"
            left={2}
          />
        </Roundel>
      </ResetButton>

      <ResetButton
        type="button"
        data-testid="pagination-last-page"
        data-page={nbPages}
        onClick={onClick}
        disabled={currentRefinement === nbPages}
      >
        <Roundel
          disableOpacity
          filled
          size={48}
          fontSize={30}
          color={
            currentRefinement === nbPages
              ? colors['pepper-100']
              : colors.aubergine
          }
          disabled={currentRefinement === nbPages}
        >
          <Icon
            type="chevron-right"
            color="white"
            size={20}
            position="relative"
            left={8}
          />
          <Icon
            type="chevron-right"
            color="white"
            size={20}
            left={-4}
            position="relative"
          />
        </Roundel>
      </ResetButton>
    </Stack>
  )
}

export const Pagination = connectPagination(UnconnectedPagination)
